// Câmera da frota (pedido 2026-09-16) — menu visível só quando o Super
// Master ativa o módulo Câmera pra esta empresa e quem está logado não é
// cliente final (accountType "usuario"), mesmo critério de app-sidebar.tsx
// e agenda-shell.tsx. UI e player de vídeo ao vivo (mpegts.js) copiados de
// master.camera.$imei.tsx (ferramenta de diagnóstico do Super Master),
// adaptados pra listar os veículos da PRÓPRIA empresa (ver
// fleet-camera.service.ts) em vez de um único IMEI cru.
import { createFileRoute, Link } from "@tanstack/react-router";
import { GpsInlineLoader } from "@/components/gps-loader";
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Mic,
  BellRing,
  Camera,
  Car,
  Cigarette,
  EyeOff,
  Gauge,
  HardDrive,
  Moon,
  Smartphone,
  UserX,
  Volume2,
  VolumeX,
  History,
  ImageOff,
  Info,
  Loader2,
  MapPin,
  PlayCircle,
  RefreshCw,
  ScanLine,
  Send,
  ShieldCheck,
  Square,
  TriangleAlert,
  Video,
  ScanFace,
  Upload,
  Trash2,
  UserCheck,
  UserRound,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type Mpegts from "mpegts.js";
import { APIProvider, Map, AdvancedMarker } from "@vis.gl/react-google-maps";
import type { Vehicle } from "@/types";
import { useAuth } from "@/contexts/auth-context";
import {
  useMyCompany,
  useFleetCameraVehicles,
  useFleetCameraPhotos,
  useFleetCameraAlarms,
  useVehicles,
  useVehicleImages,
} from "@/hooks/queries";
import { resolveVehicleIcon } from "@/lib/vehicle-icons";
import { resolveGpsDriveUrl } from "@/lib/api/gpsdrive-client";
import { resolveGoogleMapId } from "@/lib/google-maps-style";
import { RouteHistoryCenter } from "@/components/route-history-center";
import { VehicleSendCommandDialog } from "@/components/vehicle-send-command-dialog";
import { VehicleLogsPanel } from "@/components/vehicle-logs-panel";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  fleetCameraService,
  type FleetCameraAlarmEvent,
  type FleetCameraDriver,
  type FleetCameraIdentifyResult,
  type FleetCameraPhoto,
} from "@/services/fleet-camera.service";
import { isRestrictedAccountType } from "@/lib/account-type";
import { LicensePlate } from "@/components/license-plate";

function CameraPageGate() {
  const { gpsDriveUser, gpsDriveStatus } = useAuth();
  const { data: myCompany, isLoading: companyLoading } = useMyCompany();
  const isClientAccount = isRestrictedAccountType(gpsDriveUser?.accountType);

  if (gpsDriveStatus === "loading" || (companyLoading && !myCompany)) {
    return (
      <GpsInlineLoader className="h-[70vh]" />
    );
  }
  const cameraEnabled = myCompany?.modules.camera ?? false;
  if (isClientAccount || !cameraEnabled) {
    return (
      <div className="flex h-screen items-center justify-center bg-background p-6">
        <div className="max-w-md text-center">
          <h1 className="text-lg font-semibold">Sem permissão</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {isClientAccount
              ? "Este módulo é restrito a administradores e funcionários."
              : "O módulo Câmera não está ativo para esta empresa. Peça ao Super Master para ativá-lo."}
          </p>
        </div>
      </div>
    );
  }
  return <CameraPage />;
}

export const Route = createFileRoute("/camera")({
  component: CameraPageGate,
  head: () => ({ meta: [{ title: "Câmera — GPS Drive" }] }),
});

function CameraPage() {
  const { data: cameraVehicles, isLoading, isError, refetch, isFetching } = useFleetCameraVehicles();
  const [selectedDeviceId, setSelectedDeviceId] = useState<number | null>(null);
  const [historyVehicleId, setHistoryVehicleId] = useState<string | null>(null);
  const [commandVehicle, setCommandVehicle] = useState<Vehicle | null>(null);

  // Dados reais de frota (status/velocidade/motorista/placa/posição) — a
  // lista de "veículos com câmera" (useFleetCameraVehicles) só sabe
  // deviceId+name (ver fleet-camera.service.ts), então cruzamos aqui com
  // useVehicles() (mesma fonte do mapa/tela Veículos) pra montar a barra de
  // status igual à referência que o usuário mandou (pedido 2026-09-16).
  const { data: vehicles } = useVehicles();
  const selectedVehicle = vehicles?.find((v) => v.id === String(selectedDeviceId)) ?? null;
  const { data: vehicleImages } = useVehicleImages(selectedDeviceId != null ? [String(selectedDeviceId)] : []);
  const driverQuery = useCameraDriver(selectedDeviceId);
  const photoUrl = selectedVehicle
    ? (() => {
        const urls = vehicleImages?.[selectedVehicle.id];
        return urls ? resolveGpsDriveUrl(urls["256"] ?? urls.original) : undefined;
      })()
    : undefined;

  useEffect(() => {
    if (selectedDeviceId == null && cameraVehicles && cameraVehicles.length > 0) {
      setSelectedDeviceId(cameraVehicles[0].deviceId);
    }
  }, [cameraVehicles, selectedDeviceId]);

  if (!isLoading && !isError && (!cameraVehicles || cameraVehicles.length === 0)) {
    return <CameraEmptyState onRefresh={() => refetch()} isFetching={isFetching} />;
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold tracking-tight">Câmeras</h1>
            <Badge
              variant="outline"
              className="border-transparent bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400"
            >
              BETA
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground">Vídeo ao vivo e fotos de alarme dos veículos com câmera.</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw className={`mr-2 h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
          Atualizar
        </Button>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Carregando…</p>
      ) : isError ? (
        <p className="text-sm text-destructive">Falha ao carregar os veículos com câmera.</p>
      ) : (
        <>
          <Select
            value={selectedDeviceId != null ? String(selectedDeviceId) : undefined}
            onValueChange={(v) => setSelectedDeviceId(Number(v))}
          >
            <SelectTrigger className="w-full sm:w-80">
              <SelectValue placeholder="Selecione um veículo" />
            </SelectTrigger>
            <SelectContent>
              {(cameraVehicles ?? []).map((v) => (
                <SelectItem key={v.deviceId} value={String(v.deviceId)}>
                  {v.name ?? `Veículo #${v.deviceId}`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {selectedDeviceId != null && (
            <>
              {selectedVehicle && (
                <VehicleInfoBar
                  vehicle={selectedVehicle}
                  photoUrl={photoUrl}
                  driver={driverQuery.data?.driver ?? null}
                  onHistory={() => setHistoryVehicleId(selectedVehicle.id)}
                  onCommand={() => setCommandVehicle(selectedVehicle)}
                />
              )}

              <div className="grid gap-4 lg:grid-cols-3">
                <div className="lg:col-span-2">
                  <CameraFeedCard deviceId={selectedDeviceId} />
                </div>
                <LocationCard
                  vehicle={selectedVehicle}
                  onShowHistory={() => selectedVehicle && setHistoryVehicleId(selectedVehicle.id)}
                />
              </div>

              <CameraAlarmsCard deviceId={selectedDeviceId} />

              {selectedVehicle && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Eventos recentes</CardTitle>
                    <CardDescription>Telemetria e alarmes reais do rastreador deste veículo.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <VehicleLogsPanel vehicle={selectedVehicle} hours={72} />
                  </CardContent>
                </Card>
              )}

              <PhotosCard deviceId={selectedDeviceId} />
            </>
          )}
        </>
      )}

      <RouteHistoryCenter
        vehicleId={historyVehicleId}
        vehicles={vehicles ?? []}
        onClose={() => setHistoryVehicleId(null)}
      />
      <VehicleSendCommandDialog
        vehicle={commandVehicle}
        open={!!commandVehicle}
        onOpenChange={(o) => !o && setCommandVehicle(null)}
      />
    </div>
  );
}

// Barra de status do veículo selecionado (pedido 2026-09-16, layout de
// referência) — foto/placa/status vêm do MESMO merge Traccar+vehicle_metadata
// que a tela Veículos usa (useVehicles/useVehicleImages), nunca inventados.
function VehicleInfoBar({
  vehicle,
  photoUrl,
  driver,
  onHistory,
  onCommand,
}: {
  vehicle: Vehicle;
  photoUrl?: string;
  driver: FleetCameraDriver | null;
  onHistory: () => void;
  onCommand: () => void;
}) {
  const icon = resolveVehicleIcon(vehicle);
  const dotClass =
    vehicle.status === "online" ? "bg-emerald-500" : vehicle.status === "idle" ? "bg-amber-500" : "bg-rose-500";
  const statusLabel = vehicle.status === "online" ? "Online" : vehicle.status === "idle" ? "Parado" : "Offline";
  const statusBadgeClass =
    vehicle.status === "online"
      ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400"
      : vehicle.status === "idle"
        ? "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400"
        : "bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-400";

  return (
    <Card>
      <CardContent className="flex flex-wrap items-center gap-4 p-4">
        <div className="relative h-12 w-12 shrink-0 overflow-hidden rounded-full bg-muted ring-1 ring-border">
          <img src={photoUrl ?? icon.url} alt="" className="h-full w-full object-cover" />
          <span className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full ring-2 ring-background ${dotClass}`} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="truncate font-semibold">{vehicle.name}</p>
            <LicensePlate plate={vehicle.plate} size="xs" />
            <Badge variant="outline" className={`border-transparent ${statusBadgeClass}`}>
              {statusLabel}
            </Badge>
          </div>
          <p className="truncate text-xs text-muted-foreground">
            {driver
              ? `Motorista: ${driver.name}${driver.faceUrl ? " · facial cadastrada" : ""}`
              : vehicle.driver
                ? `Motorista: ${vehicle.driver}`
                : "Sem motorista vinculado"}
          </p>
        </div>
        <div className="flex items-center gap-5 text-sm">
          <div className="text-center">
            <p className="font-semibold leading-none">{Math.round(vehicle.speed)} km/h</p>
            <p className="mt-1 text-xs text-muted-foreground">Velocidade</p>
          </div>
          <div className="text-center">
            <p className="font-semibold leading-none">
              {new Date(vehicle.lastConnection).toLocaleString("pt-BR", {
                day: "2-digit",
                month: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">Última atualização</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={onHistory}>
            <History className="mr-2 h-4 w-4" />
            Histórico
          </Button>
          <Button variant="outline" size="sm" onClick={onCommand}>
            <Send className="mr-2 h-4 w-4" />
            Comando
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Sem endereço reverso-geocodificado pra frota inteira (custaria uma
// chamada extra por veículo só pra esta tela) — mas o MAPA em si (pedido
// 2026-09-16, "em localização eu quero que apareça o mapa") é real:
// mesmo Google Maps JS API do mapa principal (@vis.gl/react-google-maps),
// só que num card pequeno com 1 marcador. Endereço completo por trecho já
// existe no Histórico (RouteHistoryCenter), que este card abre de verdade.
function LocationCard({ vehicle, onShowHistory }: { vehicle: Vehicle | null; onShowHistory: () => void }) {
  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <MapPin className="h-4 w-4 text-muted-foreground" />
          Localização
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {vehicle ? (
          <>
            <VehicleMiniMap key={vehicle.id} lat={vehicle.lat} lng={vehicle.lng} />
            <p className="text-xs text-muted-foreground">
              Atualizado em{" "}
              {new Date(vehicle.lastConnection).toLocaleString("pt-BR", {
                day: "2-digit",
                month: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </p>
            <div className="flex flex-wrap gap-2">
              <Button asChild size="sm" variant="outline">
                <Link to="/" search={{ vehicle: vehicle.id }}>
                  <MapPin className="mr-2 h-4 w-4" />
                  Ver no mapa
                </Link>
              </Button>
              <Button size="sm" variant="outline" onClick={onShowHistory}>
                <History className="mr-2 h-4 w-4" />
                Rota
              </Button>
            </div>
          </>
        ) : (
          <p className="text-sm text-muted-foreground">Sem posição disponível para este veículo.</p>
        )}
      </CardContent>
    </Card>
  );
}

// Mini mapa interativo (1 veículo, sem deck.gl/clustering) — próprio
// APIProvider local, mesmo padrão de street-view-panorama.tsx: o loader do
// script do Google Maps é módulo-singleton (ver GoogleMapsPreloadMount em
// __root.tsx), então múltiplos <APIProvider> na mesma tela reaproveitam o
// mesmo script, nunca conflitam. `key={vehicle.id}` no card acima remonta
// este componente ao trocar de veículo, pra recentralizar (defaultCenter é
// "descontrolado": não persegue toda atualização de posição a cada 15s,
// senão o usuário nunca conseguiria arrastar/dar zoom no mini mapa).
function VehicleMiniMap({ lat, lng }: { lat: number; lng: number }) {
  const apiKey = (import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined) ?? "";
  if (!apiKey) {
    return (
      <div className="flex h-40 w-full items-center justify-center rounded-lg border border-dashed bg-muted/40 text-xs text-muted-foreground">
        Mapa indisponível (chave do Google Maps não configurada).
      </div>
    );
  }
  return (
    <APIProvider apiKey={apiKey}>
      <div className="h-40 w-full overflow-hidden rounded-lg">
        <Map
          mapId={resolveGoogleMapId()}
          defaultCenter={{ lat, lng }}
          defaultZoom={15}
          gestureHandling="greedy"
          disableDefaultUI
          zoomControl
          style={{ height: "100%", width: "100%" }}
        >
          <AdvancedMarker position={{ lat, lng }} />
        </Map>
      </div>
    </APIProvider>
  );
}

// Empty state (pedido 2026-09-16, layout de referência fornecido pelo
// usuário) — reaparece toda vez que a empresa ainda não vinculou nenhum
// veículo a uma câmera. Estatísticas ficam sempre zeradas aqui de propósito
// (não há veículo com câmera para contar).
function CameraEmptyState({ onRefresh, isFetching }: { onRefresh: () => void; isFetching: boolean }) {
  return (
    <div className="mx-auto max-w-5xl space-y-6 p-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold tracking-tight">Câmeras</h1>
            <Badge
              variant="outline"
              className="border-transparent bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400"
            >
              BETA
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground">Vídeo ao vivo e fotos de alarme dos veículos com câmera.</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={onRefresh} disabled={isFetching}>
            <RefreshCw className={`mr-2 h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
            Atualizar dados
          </Button>
          <span className="hidden items-center gap-1.5 text-xs text-muted-foreground sm:flex">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
            Última atualização: agora
          </span>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <CameraStatCard
          icon={Camera}
          iconClassName="bg-emerald-100 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-400"
          value={0}
          label="Câmeras ativas"
          hint="Transmitindo normalmente"
        />
        <CameraStatCard
          icon={Car}
          iconClassName="bg-slate-100 text-slate-600 dark:bg-slate-500/15 dark:text-slate-300"
          value={0}
          label="Veículos com câmera"
          hint="Do total da frota"
        />
        <CameraStatCard
          icon={TriangleAlert}
          iconClassName="bg-red-100 text-red-600 dark:bg-red-500/15 dark:text-red-400"
          value={0}
          label="Alertas hoje"
          hint="Eventos com vídeo"
        />
      </div>

      <Card className="overflow-hidden border-dashed">
        <CardContent className="grid gap-6 p-6 md:grid-cols-[1.1fr_0.9fr] md:items-center">
          <div className="space-y-4">
            <p className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-emerald-600 dark:text-emerald-400">
              <Video className="h-3.5 w-3.5" />
              Câmeras veiculares
            </p>
            <h2 className="text-xl font-bold tracking-tight sm:text-2xl">Mais segurança para sua frota</h2>
            <p className="text-sm text-muted-foreground">
              Monitore seus veículos com vídeo ao vivo, registre imagens de alarmes e tenha mais controle e
              segurança no seu dia a dia.
            </p>
            <Button asChild className="bg-emerald-600 hover:bg-emerald-700">
              <Link to="/">
                <Camera className="mr-2 h-4 w-4" />
                Cadastrar câmera
              </Link>
            </Button>
            <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              Você ainda não possui nenhuma câmera vinculada aos seus veículos.
            </p>
          </div>
          <div className="space-y-2 rounded-lg border border-border bg-muted/40 p-4">
            <CameraFeatureRow icon={Video} text="Gravação de eventos" />
            <CameraFeatureRow icon={TriangleAlert} text="Alertas com vídeo" />
            <CameraFeatureRow icon={ShieldCheck} text="Mais segurança" />
          </div>
        </CardContent>
      </Card>

      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-semibold">Como funciona?</h3>
          <p className="text-sm text-muted-foreground">
            Em poucos passos você já consegue visualizar as câmeras dos seus veículos.
          </p>
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          <CameraStepCard
            step={1}
            icon={Camera}
            title="Instale a câmera"
            description="Instale o equipamento no veículo e faça a ligação corretamente."
          />
          <CameraStepCard
            step={2}
            icon={ScanLine}
            title="Cadastre o IMEI"
            description="Vincule o IMEI da câmera ao veículo no sistema."
          />
          <CameraStepCard
            step={3}
            icon={PlayCircle}
            title="Acesse o vídeo"
            description="Veja as imagens ao vivo e eventos registrados."
          />
        </div>
      </div>

      <div className="flex items-start gap-3 rounded-lg border border-border bg-muted/40 p-4 text-sm">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
        <p className="text-muted-foreground">
          Para adicionar uma câmera, acesse{" "}
          <span className="font-medium text-foreground">Veículos → Editar veículo → Rastreador</span> e informe o
          IMEI da câmera. Se a câmera também for o rastreador do veículo, marque o tipo do rastreador como{" "}
          <span className="font-medium text-foreground">"Câmera"</span> — o mesmo IMEI identifica os dois ao mesmo
          tempo.
        </p>
      </div>
    </div>
  );
}

function CameraStatCard({
  icon: Icon,
  iconClassName,
  value,
  label,
  hint,
}: {
  icon: LucideIcon;
  iconClassName: string;
  value: number;
  label: string;
  hint: string;
}) {
  return (
    <Card>
      <CardContent className="flex items-center gap-3 p-4">
        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${iconClassName}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <p className="text-2xl font-bold leading-none">{value}</p>
          <p className="text-sm font-medium">{label}</p>
          <p className="text-xs text-muted-foreground">{hint}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function CameraFeatureRow({ icon: Icon, text }: { icon: LucideIcon; text: string }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      <Icon className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
      <span>{text}</span>
    </div>
  );
}

function CameraStepCard({
  step,
  icon: Icon,
  title,
  description,
}: {
  step: number;
  icon: LucideIcon;
  title: string;
  description: string;
}) {
  return (
    <Card>
      <CardContent className="flex items-start gap-3 p-4">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-sm font-bold text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400">
          {step}
        </div>
        <div>
          <div className="mb-1 flex items-center gap-1.5">
            <Icon className="h-4 w-4 text-muted-foreground" />
            <p className="text-sm font-semibold">{title}</p>
          </div>
          <p className="text-xs text-muted-foreground">{description}</p>
        </div>
      </CardContent>
    </Card>
  );
}

type LiveState = "idle" | "connecting" | "playing" | "error";

// Um card só, abrindo na câmera Interna (canal 2) com seletor pra trocar
// pra Externa (canal 1) — pedido 2026-10-12: "primeiro tem que aparecer a
// câmera de dentro e ter uma parte pra trocar pra câmera externa" (antes,
// 2026-09-16, eram dois cards lado a lado). Trocar de canal derruba a
// transmissão atual (efeito de limpeza abaixo). mpegts.js: carregado sob
// demanda (só quando alguém aperta "Ver ao vivo") — evita puxar a lib no
// bundle principal do app.
const CAMERA_CHANNELS = [
  { channel: 2, label: "Interna", description: "Cabine — motorista e passageiros" },
  { channel: 1, label: "Externa", description: "Frontal — visão da estrada" },
] as const;

function CameraFeedCard({ deviceId }: { deviceId: number }) {
  const [channel, setChannel] = useState<1 | 2>(2);
  const current = CAMERA_CHANNELS.find((c) => c.channel === channel) ?? CAMERA_CHANNELS[0];
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const playerRef = useRef<Mpegts.Player | null>(null);
  const [state, setState] = useState<LiveState>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // Som da cabine (2026-09-28) — pedido à parte porque liga o microfone da
  // câmera e demora alguns segundos a mais pra conectar; sem ele, o vídeo
  // continua exatamente como antes.
  const [withAudio, setWithAudio] = useState(false);

  const stop = () => {
    playerRef.current?.pause();
    playerRef.current?.unload();
    playerRef.current?.detachMediaElement();
    playerRef.current?.destroy();
    playerRef.current = null;
    setState("idle");
  };

  useEffect(() => stop, [deviceId, channel]);

  const start = async (audio = withAudio) => {
    stop();
    setWithAudio(audio);
    setErrorMessage(null);
    setState("connecting");
    try {
      const mpegts = (await import("mpegts.js")).default;
      if (!mpegts.isSupported() || !videoRef.current) {
        setState("error");
        setErrorMessage("Este navegador não suporta reprodução de vídeo ao vivo (Media Source Extensions).");
        return;
      }
      const { url, headers } = fleetCameraService.getLiveVideoRequest(deviceId, channel, audio);
      const player = mpegts.createPlayer({ type: "flv", isLive: true, url }, { headers });
      playerRef.current = player;
      player.on(mpegts.Events.ERROR, (_type: unknown, _detail: unknown, info: unknown) => {
        setState("error");
        setErrorMessage(`Falha na transmissão — a câmera pode não ter aceitado o pedido de vídeo. Detalhe: ${JSON.stringify(info)}`);
      });
      player.on(mpegts.Events.LOADING_COMPLETE, () => setState("idle"));
      // O clique no botão é o "gesto do usuário" que o navegador exige pra
      // tocar com som.
      videoRef.current.muted = !audio;
      player.attachMediaElement(videoRef.current);
      player.load();
      await player.play();
      setState("playing");
    } catch (err) {
      setState("error");
      setErrorMessage(err instanceof Error ? err.message : "Falha desconhecida ao iniciar o player.");
    }
  };

  // Quadro atual do vídeo como JPEG — usado pela facial do motorista
  // (cadastrar/identificar pela câmera interna).
  const grabFrame = (): Promise<File | null> =>
    new Promise((resolve) => {
      const video = videoRef.current;
      if (!video || video.readyState < 2 || !video.videoWidth) return resolve(null);
      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext("2d");
      if (!ctx) return resolve(null);
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(
        (blob) => resolve(blob ? new File([blob], `cabine-${Date.now()}.jpg`, { type: "image/jpeg" }) : null),
        "image/jpeg",
        0.92,
      );
    });

  // Captura real do frame atual (canvas → JPEG) e baixa no navegador — não
  // depende de nenhum endpoint novo no backend, só do <video> já conectado.
  const handleSnapshot = () => {
    const video = videoRef.current;
    if (!video || video.readyState < 2) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `camera-${deviceId}-ch${channel}-${Date.now()}.jpg`;
        a.click();
        URL.revokeObjectURL(url);
      },
      "image/jpeg",
      0.92,
    );
  };

  return (
    <Card className="overflow-hidden">
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0 pb-3">
        <div>
          <CardTitle className="text-base">Câmera {current.label}</CardTitle>
          <CardDescription className="text-xs">{current.description}</CardDescription>
        </div>
        <div className="flex items-center gap-2">
          <div role="tablist" aria-label="Escolher câmera" className="inline-flex rounded-lg border bg-muted p-0.5">
            {CAMERA_CHANNELS.map((c) => (
              <button
                key={c.channel}
                type="button"
                role="tab"
                aria-selected={channel === c.channel}
                onClick={() => setChannel(c.channel)}
                className={
                  channel === c.channel
                    ? "rounded-md bg-background px-3 py-1 text-xs font-semibold text-foreground shadow-sm"
                    : "rounded-md px-3 py-1 text-xs font-medium text-muted-foreground hover:text-foreground"
                }
              >
                {c.label}
              </button>
            ))}
          </div>
          <LiveBadge state={state} />
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="relative flex aspect-video items-center justify-center overflow-hidden rounded-lg bg-black">
          <video ref={videoRef} className="h-full w-full object-cover" muted playsInline controls={state === "playing"} />
          {state === "connecting" && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/70 text-white">
              <Loader2 className="h-6 w-6 animate-spin" />
              <p className="text-sm">{withAudio ? "Conectando vídeo e áudio da cabine…" : "Aguardando a câmera conectar…"}</p>
            </div>
          )}
          {state === "playing" && withAudio && (
            <span className="absolute left-2 top-2 inline-flex items-center gap-1 rounded-full bg-black/60 px-2 py-0.5 text-[11px] font-semibold text-white">
              <Volume2 className="h-3 w-3" /> Com áudio
            </span>
          )}
          {state === "idle" && (
            <p className="absolute px-4 text-center text-xs text-muted-foreground">
              Toque em "Ver ao vivo" para conectar (~15s).
            </p>
          )}
        </div>
        {state === "error" && errorMessage && (
          <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
            <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{errorMessage}</span>
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          {state === "playing" || state === "connecting" ? (
            <Button variant="outline" size="sm" onClick={stop}>
              <Square className="mr-2 h-4 w-4" />
              Parar
            </Button>
          ) : (
            <>
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => start(false)}>
                <Video className="mr-2 h-4 w-4" />
                Ver ao vivo
              </Button>
              <Button size="sm" variant="outline" onClick={() => start(true)} title="Vídeo com o som da cabine (motorista falando)">
                <Volume2 className="mr-2 h-4 w-4" />
                Ver com áudio
              </Button>
            </>
          )}
          {state === "playing" && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => start(!withAudio)}
              title={withAudio ? "Reconecta só com vídeo" : "Reconecta com o som da cabine"}
            >
              {withAudio ? <VolumeX className="mr-2 h-4 w-4" /> : <Volume2 className="mr-2 h-4 w-4" />}
              {withAudio ? "Sem áudio" : "Ouvir cabine"}
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={handleSnapshot} disabled={state !== "playing"}>
            <Camera className="mr-2 h-4 w-4" />
            Tirar foto
          </Button>
        </div>
        {channel === 2 && <TalkToDriver deviceId={deviceId} />}
        <DriverFacePanel
          deviceId={deviceId}
          canGrab={state === "playing" && channel === 2}
          onNeedCabin={() => {
            if (channel !== 2) setChannel(2);
            else if (state !== "playing" && state !== "connecting") void start(false);
          }}
          grabFrame={grabFrame}
        />
      </CardContent>
    </Card>
  );
}

function useCameraDriver(deviceId: number | null) {
  return useQuery({
    queryKey: ["fleet-camera-driver", deviceId],
    queryFn: () => fleetCameraService.getDriver(deviceId as number),
    enabled: deviceId != null,
    staleTime: 30_000,
  });
}

// Facial do motorista (pedido 2026-10-22). A WY02 não tem cadastro de rosto
// documentado, então quem reconhece é a plataforma: a facial fica no
// cadastro do motorista (Gestão de Frota) e "Identificar" compara um quadro
// da câmera interna com ela por IA (chaves do Assistente IA da empresa).
function DriverFacePanel({
  deviceId,
  canGrab,
  onNeedCabin,
  grabFrame,
}: {
  deviceId: number;
  canGrab: boolean;
  onNeedCabin: () => void;
  grabFrame: () => Promise<File | null>;
}) {
  const queryClient = useQueryClient();
  const { data, isLoading } = useCameraDriver(deviceId);
  const driver = data?.driver ?? null;
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<FleetCameraIdentifyResult | null>(null);

  useEffect(() => {
    setError(null);
    setResult(null);
  }, [deviceId]);

  const setDriver = (d: FleetCameraDriver) =>
    queryClient.setQueryData(["fleet-camera-driver", deviceId], { driver: d });

  const upload = useMutation({
    mutationFn: (file: File) => fleetCameraService.uploadFace(deviceId, file),
    onMutate: () => {
      setError(null);
      setResult(null);
    },
    onSuccess: (r) => setDriver(r.driver),
    onError: (e) => setError(e instanceof Error ? e.message : "Falha ao cadastrar a facial"),
  });
  const remove = useMutation({
    mutationFn: () => fleetCameraService.deleteFace(deviceId),
    onSuccess: (r) => {
      setDriver(r.driver);
      setResult(null);
    },
    onError: (e) => setError(e instanceof Error ? e.message : "Falha ao remover a facial"),
  });
  const identify = useMutation({
    mutationFn: (file: File) => fleetCameraService.identify(deviceId, file),
    onMutate: () => {
      setError(null);
      setResult(null);
    },
    onSuccess: (r) => setResult(r),
    onError: (e) => setError(e instanceof Error ? e.message : "Falha ao identificar o motorista"),
  });

  const fromCabin = async (action: "register" | "identify") => {
    if (!canGrab) {
      onNeedCabin();
      setError('Abra a câmera "Interna" ao vivo e tente de novo assim que a imagem aparecer.');
      return;
    }
    const frame = await grabFrame();
    if (!frame) {
      setError("Ainda não há imagem da câmera interna. Aguarde o vídeo aparecer.");
      return;
    }
    if (action === "register") upload.mutate(frame);
    else identify.mutate(frame);
  };

  const busy = upload.isPending || identify.isPending || remove.isPending;
  const avatar = driver?.faceUrl ?? driver?.photoUrl;

  return (
    <div className="rounded-lg border p-3">
      <div className="flex items-center gap-2 text-sm font-semibold">
        <ScanFace className="h-4 w-4 text-primary" /> Motorista e facial
      </div>
      {isLoading ? (
        <p className="mt-2 text-xs text-muted-foreground">Carregando motorista…</p>
      ) : !driver ? (
        <p className="mt-2 text-xs text-muted-foreground">
          Nenhum motorista vinculado a este veículo. Vincule em <strong>Gestão de Frota → Motoristas</strong> (campo
          "Veículo") para cadastrar a facial.
        </p>
      ) : (
        <>
          <div className="mt-2 flex items-center gap-3">
            <div className="h-14 w-14 shrink-0 overflow-hidden rounded-full bg-muted ring-1 ring-border">
              {avatar ? (
                <img src={resolveGpsDriveUrl(avatar)} alt="" className="h-full w-full object-cover" />
              ) : (
                <UserRound className="m-3.5 h-7 w-7 text-muted-foreground" />
              )}
            </div>
            <div className="min-w-0">
              <p className="truncate font-semibold">{driver.name}</p>
              <p className="text-xs text-muted-foreground">
                {driver.faceUrl
                  ? `Facial cadastrada${driver.faceUpdatedAt ? ` em ${new Date(driver.faceUpdatedAt).toLocaleDateString("pt-BR")}` : ""}`
                  : "Sem facial cadastrada"}
              </p>
            </div>
          </div>

          <div className="mt-3 flex flex-wrap gap-2">
            <Button size="sm" variant={driver.faceUrl ? "outline" : "default"} disabled={busy} onClick={() => void fromCabin("register")}>
              {upload.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ScanFace className="mr-2 h-4 w-4" />}
              {driver.faceUrl ? "Refazer facial pela câmera" : "Criar facial pela câmera"}
            </Button>
            <Button size="sm" variant="outline" disabled={busy} onClick={() => fileRef.current?.click()}>
              <Upload className="mr-2 h-4 w-4" /> Enviar foto
            </Button>
            {driver.faceUrl && (
              <>
                <Button
                  size="sm"
                  className="bg-emerald-600 hover:bg-emerald-700"
                  disabled={busy}
                  onClick={() => void fromCabin("identify")}
                >
                  {identify.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <UserCheck className="mr-2 h-4 w-4" />}
                  Identificar motorista
                </Button>
                <Button size="sm" variant="ghost" disabled={busy} onClick={() => remove.mutate()} title="Remover facial">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              capture="user"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = "";
                if (f) upload.mutate(f);
              }}
            />
          </div>
          {!canGrab && (
            <p className="mt-2 text-[11px] text-muted-foreground">
              Para usar a câmera do veículo, abra a câmera <strong>Interna</strong> ao vivo com o motorista olhando para ela.
            </p>
          )}
        </>
      )}

      {error && (
        <div className="mt-2 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-2 text-xs text-destructive">
          <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}
      {result && (
        <div
          className={
            "mt-2 rounded-md border p-2 text-xs " +
            (!result.facePresent || result.samePerson === null
              ? "border-amber-300 bg-amber-50 text-amber-800 dark:bg-amber-500/10 dark:text-amber-300"
              : result.samePerson
                ? "border-emerald-300 bg-emerald-50 text-emerald-800 dark:bg-emerald-500/10 dark:text-emerald-300"
                : "border-rose-300 bg-rose-50 text-rose-800 dark:bg-rose-500/10 dark:text-rose-300")
          }
        >
          <p className="font-semibold">
            {!result.facePresent
              ? "Nenhum rosto visível na câmera interna agora"
              : result.samePerson === null
                ? "Não foi possível confirmar quem está ao volante"
                : result.samePerson
                  ? `É ${result.driver.name} ao volante (${result.confidence}% de confiança)`
                  : `Não é ${result.driver.name} — outra pessoa ao volante (${result.confidence}% de confiança)`}
          </p>
          {result.reason && <p className="mt-0.5 opacity-80">{result.reason}</p>}
          <p className="mt-0.5 opacity-60">Conferido às {new Date(result.checkedAt).toLocaleTimeString("pt-BR")}</p>
        </div>
      )}
    </div>
  );
}

type TalkState = "idle" | "recording" | "sending" | "sent" | "error";

const TALK_MAX_SECONDS = 30;

// Float32 na taxa do navegador (44,1/48 kHz) → PCM 16-bit 8 kHz mono. Média
// por bloco em vez de pegar 1 amostra a cada N: já funciona como filtro
// passa-baixa simples e evita o chiado de aliasing na voz.
function toPcm8k(samples: Float32Array, sampleRate: number): ArrayBuffer {
  const ratio = sampleRate / 8000;
  const length = Math.floor(samples.length / ratio);
  const out = new Int16Array(length);
  for (let i = 0; i < length; i++) {
    const from = Math.floor(i * ratio);
    const to = Math.min(samples.length, Math.max(from + 1, Math.floor((i + 1) * ratio)));
    let sum = 0;
    for (let j = from; j < to; j++) sum += samples[j];
    const value = Math.max(-1, Math.min(1, sum / (to - from)));
    out[i] = Math.round(value * 32767);
  }
  return out.buffer;
}

// Falar com o motorista (2026-09-30): segura, fala, solta — o áudio toca no
// alto-falante da câmera. Não depende do vídeo estar aberto (o servidor
// abre o canal de voz sozinho; a 1ª vez leva até ~15 s).
function TalkToDriver({ deviceId }: { deviceId: number }) {
  const [state, setState] = useState<TalkState>("idle");
  const [message, setMessage] = useState<string | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const chunksRef = useRef<Float32Array[]>([]);
  const recordingRef = useRef(false);
  const limitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const releaseMic = () => {
    if (limitTimerRef.current) clearTimeout(limitTimerRef.current);
    limitTimerRef.current = null;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    void audioCtxRef.current?.close();
    audioCtxRef.current = null;
  };

  useEffect(
    () => () => {
      recordingRef.current = false;
      releaseMic();
    },
    [deviceId],
  );

  const stopAndSend = async () => {
    if (!recordingRef.current) return;
    recordingRef.current = false;
    const sampleRate = audioCtxRef.current?.sampleRate ?? 48000;
    releaseMic();
    const total = chunksRef.current.reduce((sum, c) => sum + c.length, 0);
    const merged = new Float32Array(total);
    let offset = 0;
    for (const chunk of chunksRef.current) {
      merged.set(chunk, offset);
      offset += chunk.length;
    }
    chunksRef.current = [];
    if (total < sampleRate * 0.4) {
      setState("error");
      setMessage("Muito curto — segure o botão enquanto fala.");
      return;
    }
    setState("sending");
    setMessage(null);
    try {
      const result = await fleetCameraService.sendTalk(deviceId, toPcm8k(merged, sampleRate));
      setState(result.ok ? "sent" : "error");
      setMessage(result.message);
    } catch (err) {
      setState("error");
      setMessage(err instanceof Error ? err.message : "Não foi possível enviar o áudio.");
    }
  };

  const startRecording = async () => {
    if (recordingRef.current || state === "sending") return;
    recordingRef.current = true;
    chunksRef.current = [];
    setMessage(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      // Soltou o botão enquanto o navegador pedia permissão do microfone.
      if (!recordingRef.current) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      streamRef.current = stream;
      const audioCtx = new AudioContext();
      audioCtxRef.current = audioCtx;
      const source = audioCtx.createMediaStreamSource(stream);
      const processor = audioCtx.createScriptProcessor(4096, 1, 1);
      processor.onaudioprocess = (e) => {
        if (recordingRef.current) chunksRef.current.push(new Float32Array(e.inputBuffer.getChannelData(0)));
      };
      source.connect(processor);
      processor.connect(audioCtx.destination);
      limitTimerRef.current = setTimeout(() => void stopAndSend(), TALK_MAX_SECONDS * 1000);
      setState("recording");
    } catch {
      recordingRef.current = false;
      releaseMic();
      setState("error");
      setMessage("Não foi possível usar o microfone — confira a permissão do navegador.");
    }
  };

  return (
    <div className="rounded-lg border border-border bg-muted/30 p-3">
      <div className="flex flex-wrap items-center gap-3">
        <Button
          size="sm"
          variant={state === "recording" ? "destructive" : "outline"}
          disabled={state === "sending"}
          className="touch-none select-none"
          onPointerDown={(e) => {
            e.preventDefault();
            void startRecording();
          }}
          onPointerUp={() => void stopAndSend()}
          onPointerLeave={() => void stopAndSend()}
          onPointerCancel={() => void stopAndSend()}
          onContextMenu={(e) => e.preventDefault()}
        >
          {state === "sending" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mic className="mr-2 h-4 w-4" />}
          {state === "recording"
            ? "Gravando… solte para enviar"
            : state === "sending"
              ? "Enviando para a câmera…"
              : "Segure para falar com o motorista"}
        </Button>
        {message && (
          <span className={`text-xs ${state === "error" ? "text-destructive" : "text-muted-foreground"}`}>{message}</span>
        )}
      </div>
      <p className="mt-2 text-[11px] text-muted-foreground">
        O áudio toca no alto-falante da câmera. A primeira fala pode levar uns 15 s para sair (a câmera abre o canal de
        voz); até {TALK_MAX_SECONDS} s por vez.
      </p>
    </div>
  );
}

function LiveBadge({ state }: { state: LiveState }) {
  if (state === "playing") {
    return (
      <Badge className="border-transparent bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400">
        <span className="mr-1.5 inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" />
        AO VIVO
      </Badge>
    );
  }
  if (state === "connecting") return <Badge variant="outline">Conectando…</Badge>;
  if (state === "error") {
    return (
      <Badge variant="outline" className="border-transparent bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-400">
        Erro
      </Badge>
    );
  }
  return <Badge variant="outline">Offline</Badge>;
}

// Alertas da câmera (pedido 2026-09-28: "a câmera não está puxando os
// alertas de quando está no celular etc") — vêm do servidor JT808, que
// decodifica os alarmes que a câmera manda junto com a posição.
const ALARM_ICONS: Record<string, LucideIcon> = {
  phone: Smartphone,
  smoking: Cigarette,
  fatigue: Moon,
  fatigue_warning: Moon,
  yawn: Moon,
  distraction: EyeOff,
  driver_absent: UserX,
  camera_blocked: EyeOff,
  overspeed: Gauge,
  speed_sign: Gauge,
  forward_collision: TriangleAlert,
  pedestrian: TriangleAlert,
  sos: BellRing,
};

function CameraAlarmsCard({ deviceId }: { deviceId: number }) {
  const { data, isLoading, isError } = useFleetCameraAlarms(deviceId);
  const { data: photos } = useFleetCameraPhotos(deviceId);
  const health = data?.health;
  const events = (data?.events ?? []).filter((e) => e.severity !== "info");
  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-2 space-y-0">
        <div>
          <CardTitle className="flex items-center gap-2 text-base">
            <BellRing className="h-4 w-4 text-amber-500" /> Alertas da câmera
          </CardTitle>
          <CardDescription>
            Celular ao volante, fadiga, cigarro, distração e alertas de estrada detectados pela câmera. Atualiza sozinho.
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {health?.storageFault && (
          <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-800 dark:text-amber-300">
            <HardDrive className="mt-0.5 h-4 w-4 shrink-0" />
            {/* Texto revisto 2026-10-12: o cartão já foi colocado, mas a câmera
                continua mandando 0x14 bit2 / 0x17=1 (erro no armazenamento 1)
                em todas as posições — então é cartão presente que ela não
                consegue usar, não "sem cartão". */}
            <span>
              <strong>A câmera não está conseguindo usar o cartão de memória (SD).</strong> O aviso de erro no cartão
              continua chegando dela (último em {new Date(health.updatedAt).toLocaleString("pt-BR")}). Com o cartão já
              colocado, formate-o pelo menu da própria câmera ou teste outro microSD (classe 10). Enquanto isso ela pode
              não gravar nem enviar os alertas e fotos do motorista.
            </span>
          </div>
        )}
        {(health?.videoSignalLoss || health?.videoOcclusion) && (
          <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-800 dark:text-amber-300">
            <EyeOff className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{health.videoOcclusion ? "Câmera tampada ou obstruída." : "Câmera sem sinal de vídeo."}</span>
          </div>
        )}
        {isLoading ? (
          <div className="flex h-24 items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : isError ? (
          <p className="text-sm text-muted-foreground">Não foi possível consultar os alertas da câmera agora.</p>
        ) : events.length === 0 ? (
          <div className="flex items-center gap-2 rounded-md border border-dashed p-4 text-sm text-muted-foreground">
            <ShieldCheck className="h-4 w-4 text-emerald-500" />
            Nenhum alerta recebido da câmera. Quando ela detectar celular, fadiga, cigarro ou distração, o alerta aparece
            aqui com a foto capturada.
          </div>
        ) : (
          <ul className="divide-y rounded-md border">
            {events.slice(0, 30).map((e) => (
              <CameraAlarmRow key={e.id} event={e} photo={photoForAlarm(e, photos)} />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

// Foto que a câmera mandou junto com o alerta (0x0801 chega logo depois da
// posição com o alarme) — a mais próxima dentro de 2 min. O servidor não
// liga os dois por ID, então o vínculo é por horário.
const ALARM_PHOTO_WINDOW_MS = 2 * 60 * 1000;

function photoForAlarm(event: FleetCameraAlarmEvent, photos: FleetCameraPhoto[] | undefined) {
  if (!photos?.length) return undefined;
  const at = new Date(event.receivedAt).getTime();
  let best: FleetCameraPhoto | undefined;
  let bestDiff = Infinity;
  for (const p of photos) {
    const diff = Math.abs(new Date(p.modifiedAt).getTime() - at);
    if (diff <= ALARM_PHOTO_WINDOW_MS && diff < bestDiff) {
      best = p;
      bestDiff = diff;
    }
  }
  return best;
}

function AlarmPhoto({ photo }: { photo: FleetCameraPhoto }) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    let url: string | null = null;
    fleetCameraService
      .fetchPhotoBlob(photo)
      .then((blob) => {
        if (cancelled) return;
        url = URL.createObjectURL(blob);
        setObjectUrl(url);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [photo]);
  if (!objectUrl) return <div className="h-12 w-16 shrink-0 animate-pulse rounded-md bg-muted" />;
  return (
    <a href={objectUrl} target="_blank" rel="noopener noreferrer" className="shrink-0" title="Abrir foto do alerta">
      <img src={objectUrl} alt="Foto do alerta" className="h-12 w-16 rounded-md object-cover" />
    </a>
  );
}

function CameraAlarmRow({ event, photo }: { event: FleetCameraAlarmEvent; photo?: FleetCameraPhoto }) {
  const Icon = ALARM_ICONS[event.type] ?? TriangleAlert;
  const critical = event.severity === "critical";
  return (
    <li className="flex items-center gap-3 px-3 py-2.5">
      <span
        className={
          critical
            ? "grid h-8 w-8 shrink-0 place-items-center rounded-full bg-red-100 text-red-600 dark:bg-red-500/15 dark:text-red-400"
            : "grid h-8 w-8 shrink-0 place-items-center rounded-full bg-amber-100 text-amber-600 dark:bg-amber-500/15 dark:text-amber-400"
        }
      >
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{event.label}</p>
        <p className="text-xs text-muted-foreground">
          {new Date(event.receivedAt).toLocaleString("pt-BR")} · {Math.round(event.speedKmh)} km/h
        </p>
      </div>
      {photo && <AlarmPhoto photo={photo} />}
      <a
        href={`https://www.google.com/maps?q=${event.latitude},${event.longitude}`}
        target="_blank"
        rel="noopener noreferrer"
        className="shrink-0 text-xs font-semibold text-primary hover:underline"
      >
        Ver local
      </a>
    </li>
  );
}

function PhotosCard({ deviceId }: { deviceId: number }) {
  const { data: photos, isLoading, isError } = useFleetCameraPhotos(deviceId);
  return (
    <Card>
      <CardHeader>
        <CardTitle>Fotos de alarme</CardTitle>
        <CardDescription>Capturadas pela câmera em eventos de alarme.</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Carregando…</p>
        ) : isError ? (
          <p className="text-sm text-destructive">Falha ao carregar as fotos.</p>
        ) : !photos || photos.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-10 text-muted-foreground">
            <ImageOff className="h-8 w-8" strokeWidth={1.5} />
            <p className="text-sm">Nenhuma foto de alarme recebida ainda.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
            {photos.map((photo) => (
              <PhotoThumb key={photo.file} photo={photo} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PhotoThumb({ photo }: { photo: FleetCameraPhoto }) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let url: string | null = null;
    fleetCameraService
      .fetchPhotoBlob(photo)
      .then((blob) => {
        if (cancelled) return;
        url = URL.createObjectURL(blob);
        setObjectUrl(url);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [photo]);

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-background/60">
      <div className="flex aspect-square items-center justify-center bg-muted">
        {failed ? (
          <ImageOff className="h-6 w-6 text-muted-foreground" strokeWidth={1.5} />
        ) : objectUrl ? (
          <img src={objectUrl} alt={photo.file} className="h-full w-full object-cover" />
        ) : (
          <div className="h-full w-full animate-pulse bg-muted-foreground/10" />
        )}
      </div>
      <div className="p-2 text-xs text-muted-foreground">{new Date(photo.modifiedAt).toLocaleString("pt-BR")}</div>
    </div>
  );
}
